import { useEffect, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useAction, useMutation, useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { StatusPill } from "@/components/status-pill"
import { Skeleton } from "@/components/ui/skeleton"
import {
  MktDialog,
  MktDialogContent,
  MktDialogFormFooter,
  MktDialogHeader,
  MktDialogTitle,
} from "@/components/mkt-dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  hackathonPlacementLabel,
  type HackathonPlacement,
} from "@/lib/hackathon-placement"
import { PaywallDialog } from "@/components/paywall-dialog"
import { HackathonFormFields } from "@/components/recruit/hackathon-form-fields"
import { formatEventDateTime, formatShortDate } from "@/lib/dates"
import { errorMessage } from "@/lib/errors"
import {
  draftFromHackathon,
  hackathonDraftComplete,
  hackathonDraftToArgs,
  hackathonWindowError,
  type HackathonDraft,
} from "@/lib/hackathon-form"
import { usePendingAction } from "@/hooks/use-pending-action"
import { toast } from "sonner"

export const Route = createFileRoute("/recruit/hackathons_/$hackathonId")({
  validateSearch: (search: Record<string, unknown>) => ({
    pay: search.pay === true || search.pay === "true",
  }),
  component: ManageHackathon,
})

function ManageHackathon() {
  const { hackathonId } = Route.useParams()
  const { pay } = Route.useSearch()
  const navigate = useNavigate()
  const reconcileCheckout = useAction(
    api.paymentIntentsActions.reconcileMyCheckout,
  )
  const h = useQuery(api.hackathons.get, {
    hackathonId: hackathonId as Id<"hackathons">,
  })
  const registrations = useQuery(
    api.hackathons.listRegistrations,
    h?.isManager ? { hackathonId: hackathonId as Id<"hackathons"> } : "skip",
  )
  const cancel = useMutation(api.hackathons.cancel)
  const setPlacement = useMutation(api.hackathons.setRegistrationPlacement)
  const [editOpen, setEditOpen] = useState(false)
  const [payOpen, setPayOpen] = useState(false)

  useEffect(() => {
    if (pay) setPayOpen(true)
  }, [pay])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("payment") !== "return") return

    // The browser return only *requests* reconciliation — the server reads
    // Razorpay directly and settles from that evidence. Nothing in this URL
    // can unlock anything.
    void (async () => {
      try {
        const result = await reconcileCheckout({
          kind: "employer_hackathon",
          hackathonId: hackathonId as Id<"hackathons">,
        })
        if (result.outcome === "settled") {
          toast.success(
            result.fulfillment === "fulfilled"
              ? "Hackathon published"
              : "Payment received — pending company approval",
          )
        } else if (result.outcome === "pending_payment") {
          toast.message("Razorpay hasn't confirmed this payment yet — this page updates when it lands.")
        } else if (result.outcome === "support_needed") {
          toast.error(result.message)
        }
      } catch (e) {
        toast.error(errorMessage(e, "Payment check failed"))
      } finally {
        void navigate({
          to: "/recruit/hackathons/$hackathonId",
          params: { hackathonId },
          search: { pay: false },
          replace: true,
        })
      }
    })()
  }, [hackathonId, navigate, reconcileCheckout])

  if (h === undefined) {
    return <Skeleton className="h-48 w-full" />
  }
  if (!h || !h.isManager) {
    return (
      <div>
        <p>Hackathon not found.</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/recruit/hackathons">Back to hackathons</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="mkt-page-title">{h.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatEventDateTime(h.startsAt)} →{" "}
            {formatEventDateTime(h.endsAt)} · {h.venue}
          </p>
          <div className="mt-2 flex gap-2">
            <StatusPill domain="hackathon" status={h.status} as="badge" />
            <Badge variant="outline">payment: {h.paymentStatus}</Badge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/recruit/hackathons">Back</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/hackathons/$hackathonId" params={{ hackathonId }}>
              Public page
            </Link>
          </Button>
          {h.status !== "cancelled" && (
            <Button variant="outline" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
          )}
          {h.status === "draft" && (
            <Button onClick={() => setPayOpen(true)}>
              Pay ₹3,000 & publish
            </Button>
          )}
          {h.status !== "cancelled" && (
            <Button
              variant="destructive"
              onClick={async () => {
                if (!window.confirm("Cancel this hackathon? This cannot be undone.")) {
                  return
                }
                try {
                  await cancel({ hackathonId: h._id })
                  toast.success("Hackathon cancelled")
                } catch (e) {
                  toast.error(errorMessage(e, "Failed"))
                }
              }}
            >
              Cancel event
            </Button>
          )}
        </div>
      </div>

      <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
        {h.description}
      </div>

      <div>
        <h2 className="mb-3 text-lg font-medium">
          Registrations
          {h.capacity !== undefined
            ? ` (${h.registrationCount}/${h.capacity})`
            : ` (${h.registrationCount})`}
        </h2>
        {h.status === "draft" ? (
          <p className="text-sm text-muted-foreground">
            Publish the hackathon to start accepting registrations.
          </p>
        ) : registrations === undefined ? (
          <Skeleton className="h-24 w-full" />
        ) : registrations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No registrations yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Resume</TableHead>
                <TableHead>Placement</TableHead>
                <TableHead>Registered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registrations.map((r) => (
                <TableRow key={r._id}>
                  <TableCell>
                    <div className="font-medium">{r.seekerName}</div>
                    {r.seekerHeadline && (
                      <div className="text-xs text-muted-foreground">
                        {r.seekerHeadline}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.seekerEmail ? (
                      <a
                        href={`mailto:${r.seekerEmail}`}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {r.seekerEmail}
                      </a>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {r.resumeUrl ? (
                      <a
                        href={r.resumeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline"
                      >
                        Resume
                      </a>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={r.placement ?? "none"}
                      onValueChange={async (value) => {
                        const placement =
                          value === "none" ? undefined : (value as HackathonPlacement)
                        try {
                          await setPlacement({
                            registrationId: r._id,
                            placement,
                          })
                          toast.success(
                            placement
                              ? `Marked ${hackathonPlacementLabel(placement)}`
                              : "Placement cleared",
                          )
                        } catch (e) {
                          toast.error(errorMessage(e, "Failed"))
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 w-[8.5rem]">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="first">1st place</SelectItem>
                        <SelectItem value="second">2nd place</SelectItem>
                        <SelectItem value="third">3rd place</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {formatShortDate(r._creationTime, true)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <EditHackathonDialog open={editOpen} onOpenChange={setEditOpen} h={h} />

      <PaywallDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        kind="employer_hackathon"
        hackathonId={h._id}
        onPaid={() => {
          toast.success("Hackathon published")
          setPayOpen(false)
          void navigate({
            to: "/recruit/hackathons/$hackathonId",
            params: { hackathonId },
            search: { pay: false },
          })
        }}
      />
    </div>
  )
}

function EditHackathonDialog({
  open,
  onOpenChange,
  h,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  h: {
    _id: Id<"hackathons">
    title: string
    description: string
    venue: string
    address: string
    city: string
    startsAt: number
    endsAt: number
    capacity?: number
    imageUrl?: string | null
  }
}) {
  const update = useMutation(api.hackathons.update)
  const [draft, setDraft] = useState<HackathonDraft>(() => draftFromHackathon(h))
  const { pending: busy, run: runSave } = usePendingAction()

  // Reset ON OPEN only. `h` must stay out of the deps: it is a live Convex
  // doc, so anything that patches it while the dialog is open (a seeker
  // registering bumps registrationCount) would otherwise re-run this and wipe
  // the organiser's in-progress edits.
  useEffect(() => {
    if (!open) return
    setDraft(draftFromHackathon(h))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const patch = (next: Partial<HackathonDraft>) =>
    setDraft((d) => ({ ...d, ...next }))

  const timingError = hackathonWindowError(draft.startsAt, draft.endsAt)

  return (
    <MktDialog open={open} onOpenChange={onOpenChange}>
      <MktDialogContent size="lg" scroll>
        <MktDialogHeader>
          <MktDialogTitle>Edit hackathon</MktDialogTitle>
        </MktDialogHeader>
        <form
          className="mkt-form space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (timingError) {
              toast.error(timingError)
              return
            }
            void runSave(
              async () => {
                await update({
                  hackathonId: h._id,
                  ...hackathonDraftToArgs(draft),
                })
                onOpenChange(false)
              },
              { success: "Hackathon updated" },
            )
          }}
        >
          <HackathonFormFields
            draft={draft}
            onPatch={patch}
            timingError={timingError}
          />
          <MktDialogFormFooter
            busy={busy}
            disabled={!hackathonDraftComplete(draft) || !!timingError}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </MktDialogContent>
    </MktDialog>
  )
}
